import * as Icons from 'lucide-svelte';
import { UserStore } from './UserStore.svelte.js';
import Dashboard from '../modules/Dashboard.svelte';
import User from '../modules/User.svelte';
import Admin from '../modules/Admin.svelte';
import Users from '../modules/Users.svelte';
import Root from '../modules/Root.svelte';
import Ai from '../modules/Ai.svelte';
import AiAnnouncement from '../modules/ai/AiAnnouncement.svelte';
import Helper from '../modules/Helper.svelte';
import StringNormizer from '../modules/helper/StringNormizer.svelte';

/**
 * @typedef {Object} NavItem
 * @property {any} icon
 * @property {string} label
 * @property {string} description
 * @property {string=} button
 * @property {string} url
 * @property {any} component
 * @property {string=} parent
 * @property {string[]=} groups
 */

/**
 * @typedef {NavItem & {children: NavTreeItem[]}} NavTreeItem
 */

class PageStoreDefinition {
    static PAGE_DASHBOARD = {
        icon: Icons.Home,
        label: 'Dashboard',
        description: 'Übersicht über alle wichtigen Informationen auf einen Blick.',
        button: 'Zum Dashboard',
        url: '/',
        component: Dashboard,
    };

    static PAGE_USER = {
        icon: Icons.User,
        label: 'Benutzerkonto',
        description: 'Verwalte Dein Benutzerkonto.',
        button: 'Zum Benutzerkonto',
        url: '/user',
        component: User,
    };

    static PAGE_ADMIN = {
        icon: Icons.ShieldCheck,
        label: 'Admin',
        description: 'Für alle administrativen Aufgaben.',
        button: 'Zum Admin-Bereich',
        url: '/admin',
        component: Admin,
        groups: ['ADMIN'],
    };

    static PAGE_ROOT = {
        icon: Icons.BottleWine,
        label: 'Gott-Modus',
        description: 'Für alle Aufgaben der heiligen Programmierer.',
        button: 'Ich bin ROOT',
        url: '/root',
        component: Root,
        parent: '/admin',
        groups: ['ROOT'],
    };

    static PAGE_USERS = {
        icon: Icons.Users,
        label: 'Benutzerverwaltung',
        description: 'Verwalte alle Benutzer.',
        button: 'Zur Benutzerverwaltung',
        url: '/users',
        component: Users,
        parent: '/admin',
        groups: ['USERS'],
    };

    static PAGE_AI = {
        icon: Icons.BotMessageSquare,
        label: 'AI',
        description: 'Für alle Aufgaben der heiligen Programmierer.',
        button: 'Zur AI-Verwaltung',
        url: '/ai',
        component: Ai,
    };

    static PAGE_AI_ANNOUNCEMENTS = {
        icon: Icons.ToolCase,
        label: 'AI-Inventur',
        description: 'Verwaltung der AI-Meldungen.',
        button: 'Zur AI-Inventur',
        url: '/ai/announcements',
        component: AiAnnouncement,
        parent: '/ai',
    };

    static PAGE_HELPER = {
        icon: Icons.Utensils,
        label: 'Helferlein',
        description: 'Kleinere Tools, die einem das Leben einfacher machen.',
        button: 'Zu den Helferlein',
        url: '/helper',
        component: Helper,
    };

    static PAGE_STRING_NORMIZER = {
        icon: Icons.PencilRuler,
        label: 'Klartext-Bereinigung',
        description: 'Texte aus KI oder externen Quellen normalisieren.',
        button: 'Zum Tool',
        url: '/strings/normizer',
        component: StringNormizer,
        parent: '/helper',
    };

    /**
     * @param {NavItem} page
     * @returns {boolean}
     */
    static isVisible(page) {
        const groups = page.groups ?? [];

        return groups.length === 0 || groups.some((group) => UserStore.hasGroup(group));
    }

    /**
     * @param {any[]} menu
     * @param {string} url
     * @returns {string}
     */
    static getPageFromUrl(menu, url) {
        return menu.some((i) => i.url === url) ? url : '/';
    }
}

class NavigationStoreClass {
    get list() {
        /** @type {NavItem[]} */
        const pages = [
            PageStoreDefinition.PAGE_DASHBOARD,
            {
                ...PageStoreDefinition.PAGE_USER,
                label: UserStore.nickName || UserStore.firstName || UserStore.userName || 'Benutzerkonto',
            },
            PageStoreDefinition.PAGE_AI,
            PageStoreDefinition.PAGE_AI_ANNOUNCEMENTS,
            PageStoreDefinition.PAGE_HELPER,
            PageStoreDefinition.PAGE_STRING_NORMIZER,
            PageStoreDefinition.PAGE_ADMIN,
            PageStoreDefinition.PAGE_USERS,
            PageStoreDefinition.PAGE_ROOT,
        ];

        return pages.filter((page) => PageStoreDefinition.isVisible(page));
    }

    get listAdmin() {
        return this.list.filter((item) => {
            const groups = Array.isArray(item.groups) ? item.groups : [];

            return item.url !== '/admin' && (groups.includes('ADMIN') || item.parent === '/admin');
        });
    }

    get tree() {
        const list = this.list;

        /** @type {NavTreeItem[]} */
        const tree = [];

        for (const item of list) {
            if (!item.parent) {
                tree.push({
                    ...item,
                    children: [],
                });
                continue;
            }

            const parent = list.find((p) => p.url === item.parent);

            if (!parent) {
                tree.push({
                    ...item,
                    children: [],
                });
            }
        }

        for (const item of list) {
            if (!item.parent) {
                continue;
            }

            const parent = tree.find((p) => p.url === item.parent);

            if (parent) {
                parent.children.push({
                    ...item,
                    children: [],
                });
            }
        }

        return tree;
    }
}

export const NavigationStore = new NavigationStoreClass();

class PageStoreClass {
    #requestedPath = $state('/');

    constructor() {
        if (typeof window !== 'undefined') {
            this.refresh();

            window.addEventListener('popstate', () => {
                this.refresh();
            });
        }
    }

    #sync() {
        this.#requestedPath = window.location.pathname;
    }

    refresh() {
        this.#sync();
    }

    get path() {
        return PageStoreDefinition.getPageFromUrl(NavigationStore.list, this.#requestedPath);
    }

    set(url) {
        this.#requestedPath = url;

        if (typeof window !== 'undefined' && window.location.pathname !== url) {
            window.history.pushState({}, '', url);
        }
    }

    get component() {
        const path = this.path;

        return NavigationStore.list.find((item) => item.url === path)?.component ?? null;
    }
}

export const PageStore = new PageStoreClass();
